import React, { useContext, useEffect, useState } from "react";
import { Text, CustomTable } from "react_reusable_components";
import { DownloadOutlined, EyeOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import debounce from "lodash.debounce";
import { useIntl } from "react-intl";

import TableActions from "../../components/TableActions";
import useResponsive from "../../core/hooks/useResponsive";
import { columns, getTMActions } from "./AuditListingColumns";
import { TwoRow } from "../../core/layouts";
import {
  downloadReports,
  getLocalizedMessage,
} from "../../UtilityFunctions/utils";
import { AuthContext } from "../../globalContext/auth/authProvider";
import { setAuditQuery } from "../../globalContext/ManageAudit/auditActions";
import {
  recordsPerPageOptions,
  PAGE_LIMIT,
  userRole,
  AUDIT_STATUSES,
  AUDIT_MEDIUM,
} from "../../constants";
import { GlobalLoaderContext } from "../../globalContext/globalLoader/globalLoaderProvider";
import Styles from "./AuditListingContainer.module.scss";
import AuditService from "../../services/audit-service";
import { AuditContext } from "../../globalContext/ManageAudit/AuditProvider";
import AuditListingTopContainer from "../AuditListingTopContainer";
import SetAuditTypeContainer from "../SetAuditTypeContainer/SetAuditTypeContainer";
import ConfirmModalContainer from "../ConfirmModalContainer/ConfirmModalContainer";
import variables from "../../themes/base/styles/variables";
import { showNotification } from "../../UtilityFunctions/notification";
import ActionWithReasonModal from "../ActionWithReasonModal/ActionWithReasonModal";
import UpdateTMModal from "../UpdateTMModal";

const AuditListingContainer = () => {
  const responsive = useResponsive();
  const [auditState, auditDispatch] = useContext(AuditContext);
  const [authState] = useContext(AuthContext);

  const loaderDispatch = useContext(GlobalLoaderContext)[1];
  const [query, setQuery] = useState({
    page: auditState?.query?.page || 1,
    recordsPerPage: auditState?.query?.recordsPerPage || PAGE_LIMIT,
    searchStr: auditState?.query?.searchStr || "",
  });
  const [filter, setFilter] = useState({
    productCategories: auditState?.query?.productCategories || [],
    subCategories: auditState?.query?.subCategories || [],
    status: auditState?.query?.status || [],
    countries: auditState?.query?.countries || [],
    currentAuditType: auditState?.query?.currentAuditType || [],
    auditCreationYear: auditState?.query?.auditCreationYear || [],
  });
  const [sortedInfo, setSortedInfo] = useState({});
  const [selectedRow, setSelectedRow] = useState(null);
  const [openActionModal, setOpenActionModal] = useState(false);
  const [openStatusChangeModal, setOpenStatusChangeModal] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isReAudit, setIsReAudit] = useState(false);

  const [activeRecord, setActiveRecord] = useState({});
  const [startAuditModal, setStartAuditModal] = useState(false);

  const navigate = useNavigate();
  const intl = useIntl();

  const [tmRecordToChange, setTmRecordToChange] = useState(null);

  useEffect(() => {
    setSelectedRow(null);
  }, [auditState?.auditList?.result]);

  useEffect(() => {
    auditDispatch(setAuditQuery({ ...auditState?.query, ...query, ...filter }));
    const debouncedApiCall = debounce(() => {
      getAuditList();
    }, 400);

    debouncedApiCall();
  }, [query, filter, sortedInfo]);

  const getAuditList = () => {
    let queryParams = getAuditListQueryParams(query, filter, sortedInfo);
    AuditService.getAuditList({
      dispatch: auditDispatch,
      loaderDispatch: loaderDispatch,
      params: queryParams,
      intl,
    });
  };

  const changeStatus = (record) => {
    setOpenStatusChangeModal(true);
    setActiveRecord(record);
  };

  const setAuditType = (record) => {
    openModalAndSetActiveRecord(record);
  };

  const sendForReAudit = (record) => {
    openModalAndSetActiveRecord(record);
    setIsReAudit(true);
  };

  const openModalAndSetActiveRecord = (record) => {
    setOpenActionModal(true);
    setActiveRecord(record);
  };

  const startAudit = (record) => {
    if (!record.auditMedium) {
      setStartAuditModal(true);
      return;
    }
    if (record.auditMedium === AUDIT_MEDIUM.REMOTE) {
      window.open(`/audit/remote/${record.id}`, "_blank");
      return;
    }
    navigate(`/audit/${record.id}/${record.auditMedium}`);
  };

  const previewAudit = (record) => {
    navigate(`/audit-preview/${record.id}/${record.auditMedium}`);
  };

  const continueAudit = (record) => {
    if (record.auditMedium === AUDIT_MEDIUM.REMOTE) {
      window.open(`/audit/remote/${record.id}`, "_blank");
      return;
    }
    navigate(`/audit/${record.id}/${record.auditMedium}`);
  };

  async function auditReAuditDateNMediumSubmit(values) {
    let objectToSend = {
      loaderDispatch,
      intl,
      body: values,
      auditId: activeRecord?.id,
    };

    try {
      if (!isReAudit) await AuditService.setAuditMediumType(objectToSend);
      else {
        await AuditService.updateAuditStatus({
          ...objectToSend,
          isReAudit: true,
        });
      }
      setActiveRecord({});
      setOpenActionModal(false);
      getAuditList();
      if (isReAudit) setIsReAudit(false);
    } catch (err) {} //NOSONAR
  }

  async function changeStatusOnSubmitFromModal(values) {
    try {
      await AuditService.updateAuditStatus({
        loaderDispatch,
        intl,
        body: values,
        auditId: activeRecord?.id,
      }).then(() => {
        getAuditList();
        setActiveRecord({});
        setOpenStatusChangeModal(false);
      });
    } catch (err) {} //NOSONAR
  }

  async function markAuditComplete(record) {
    try {
      await AuditService.updateAuditStatus({
        loaderDispatch,
        intl,
        body: { status: AUDIT_STATUSES.COMPLETED },
        auditId: record?.id,
      }).then(() => {
        getAuditList();
        setOpenModal(false);
        setActiveRecord({});
      });
    } catch (err) {
      setOpenModal(false);
    }
  }

  async function copyLink(values) {
    try {
      await AuditService.sendLinkToDnsd({
        loaderDispatch,
        intl,
        auditId: values?.id,
        copyLink: true,
      }).then(({ data }) => {
        navigator.clipboard.writeText(data.data);
      });
    } catch (err) {} //NOSONAR
  }

  async function sendLinkToDnsd(values) {
    try {
      await AuditService.sendLinkToDnsd({
        loaderDispatch,
        intl,
        auditId: values?.id,
        copyLink: false,
      }).then(() => {
        getAuditList();
        setActiveRecord({});
      });
    } catch (err) {} //NOSONAR
  }

  async function editAudit(values) {
    navigate(`/edit-audit/${values.id}/${values.auditMedium}`);
  }

  async function sendReportToSD(values) {
    sendOrDownloadReport(values);
  }

  async function downloadReport(values) {
    sendOrDownloadReport(values, true);
  }

  async function sendOrDownloadReport(values, download = false) {
    try {
      await AuditService.sendReportToSD({
        loaderDispatch,
        intl,
        auditId: values?.id,
        download,
      }).then((res) => {
        if (download) downloadReports(res);
        getAuditList();
        setActiveRecord({});
      });
    } catch (err) {} //NOSONAR
  }

  async function sendAuditPdfToSD(record) {
    try {
      await AuditService.sendReportToSD({
        loaderDispatch,
        intl,
        auditId: record?.id,
        format: "pdf",
      }).then((res) => {
        getAuditList();
      });
    } catch (err) {} //NOSONAR
  }
  const updateAuditTm = async (payload) => {
    try {
      await AuditService.updateAudit({
        loaderDispatch,
        intl,
        auditId: tmRecordToChange?.id,
        payload,
      }).then((res) => {
        getAuditList();
        setTmRecordToChange(null);
      });
    } catch (err) {} //NOSONAR
  };

  function joinVideoCallAdminNCA(record) {
    AuditService.checkParticipant({ intl, auditId: record.id }).then((res) => {
      if (res?.data?.isCallStarted)
        window.open(`/audit/remote/${record.id}`, "_blank");
      else
        showNotification(
          "error",
          "Error",
          getLocalizedMessage(intl, "error.noParticipantFound")
        );
    });
  }

  function markCompleteModal(record) {
    setOpenModal(true);
    setActiveRecord(record);
  }

  const tableActionsOptions = (record) => {
    if (authState?.currentRole?.id === userRole.TM) {
      return getTMActions(record, intl, {
        changeStatus,
        setAuditType,
        startAudit,
        continueAudit,
        sendForReAudit,
        sendLinkToDnsd,
        previewAudit,
        copyLink,
        markAuditComplete: markCompleteModal,
        editAudit,
        sendReportToSD,
        downloadReport,
        sendAuditPdfToSD,
      });
    }

    let options = [
      [AUDIT_STATUSES.AUDIT_SUBMITTED, AUDIT_STATUSES.COMPLETED].includes(
        record.status
      ) && {
        label: getLocalizedMessage(
          intl,
          "auditListing.action.downloadExcelReport"
        ),
        icon: <DownloadOutlined className={Styles["icon-style"]} />,
        action: () => downloadReport(record),
      },
      record.auditMedium === AUDIT_MEDIUM.REMOTE &&
        (record.status === AUDIT_STATUSES.IN_PROGRESS ||
          record.status === AUDIT_STATUSES.RE_AUDIT_IN_PROGRESS) && {
          label: (
            <span style={{ color: "#004386" }}>
              {getLocalizedMessage(intl, "auditListing.action.joinVideoCall")}
            </span>
          ),
          icon: (
            <img
              alt="block"
              src={"/icons/video.svg"}
              className={Styles["icon-style"]}
            />
          ),
          action: () => joinVideoCallAdminNCA(record), //NOSONAR
        },
    ].filter(Boolean);

    if (record.status !== AUDIT_STATUSES.PENDING)
      options = [
        {
          label: getLocalizedMessage(
            intl,
            "auditListing.action.viewPreviewMode"
          ),
          icon: <EyeOutlined className={Styles["icon-style"]} />,
          action: () =>
            navigate(`/audit-preview/${record?.id}/${record.auditMedium}`),
        },
        ...options,
      ];
    if (
      [AUDIT_STATUSES.PENDING, AUDIT_STATUSES.RE_AUDIT_PENDING].includes(
        record.status
      ) &&
      authState?.currentRole?.id === userRole.CA
    ) {
      options = [
        {
          label: getLocalizedMessage(intl, "auditListing.action.updateTM"),
          icon: null,
          action: () => {
            setTmRecordToChange(record);
          },
        },
      ];
    }
    if (record.status === AUDIT_STATUSES.ON_HOLD) options = [];
    return options;
  };

  function handleTableChange(_, filters, sorter) {
    setSortedInfo((prevSorter) => ({
      ...prevSorter,
      columnKey: sorter?.columnKey,
      order: sorter?.order,
    }));
  }

  const downloadAuditReportsHandler = () => {
    let queryParams = getAuditListQueryParams(query, filter, sortedInfo);

    delete queryParams.pageLimit;
    delete queryParams.pageSkip;
    if (auditState?.auditList?.result?.length)
      AuditService.getDownloadAuditReports({
        params: queryParams,
        intl,
        loaderDispatch: loaderDispatch,
      });
    else
      showNotification(
        "error",
        "Error",
        getLocalizedMessage(intl, "reports.cannotGenerateData")
      );
  };

  return (
    <>
      {tmRecordToChange?.id ? (
        <UpdateTMModal
          open={tmRecordToChange?.id}
          onSubmit={updateAuditTm}
          onClose={() => {
            setTmRecordToChange(null);
          }}
          record={tmRecordToChange}
        />
      ) : null}
      <ConfirmModalContainer
        question={getLocalizedMessage(intl, "audit.complete.confirmation")}
        open={openModal}
        descriptionText={getLocalizedMessage(
          intl,
          "audit.complete.confirmation.desc"
        )}
        setOpen={setOpenModal}
        iconSrc={"/icons/warningIcon.svg"}
        onClose={() => {
          setOpenModal(false);
        }}
        onCancel={() => {
          setOpenModal(false);
        }}
        onConfirm={() => markAuditComplete(activeRecord)}
      />
      <TwoRow
        className={Styles["main-container"]}
        topSection={
          <AuditListingTopContainer
            query={query}
            setQuery={setQuery}
            filter={filter}
            setFilter={setFilter}
            downloadAuditReportsHandler={downloadAuditReportsHandler}
          />
        }
        bottomSection={
          <CustomTable
            locale={{
              emptyText: !auditState?.auditList?.result && (
                <div style={{ minHeight: variables.customTableHeight }} />
              ),
            }}
            onRow={(record) => {
              return {
                onClick: () => {
                  if (record?.id === selectedRow) setSelectedRow(null);
                  else setSelectedRow(record.id);
                },
              };
            }}
            sticky={{ offsetHeader: 0 }}
            isMobile={!responsive.isSm}
            columns={[
              ...columns(
                intl,
                responsive,
                authState?.currentRole?.id,
                sortedInfo
              ),
              {
                title: <></>,
                dataIndex: "operation",
                key: "operation",
                align: "right",
                width: responsive.isMd && "3%",
                render: (_, record) => {
                  return tableActionsOptions(record)?.length > 0 ? (
                    <TableActions
                      openPopover={selectedRow === record.id}
                      tableActionsOptions={tableActionsOptions(record)}
                    />
                  ) : null;
                },
              },
            ]}
            dataSource={auditState?.auditList?.result?.map((data, idx) => ({
              ...data,
              key: idx,
            }))}
            query={query}
            setQuery={setQuery}
            onTableChange={handleTableChange}
            totalCount={parseInt(auditState?.auditList?.totalCount)}
            prevText={getLocalizedMessage(intl, "pagination.prevText")}
            nextText={getLocalizedMessage(intl, "pagination.nextText")}
            sizeChangeOptions={recordsPerPageOptions.map((rec) => ({
              value: rec,
              label: (
                <Text className={Styles["select-item-style"]}>
                  {rec}&nbsp;
                  {getLocalizedMessage(intl, "pagination.selectLabelSuffix")}
                </Text>
              ),
            }))}
            tableStyleClassName={`${Styles["custom-table-style"]} ${
              Styles[!responsive?.isSm && "custom-table-mobile-style"]
            } `}
          />
        }
      />
      <ActionWithReasonModal
        onClose={() => setOpenStatusChangeModal(false)}
        open={openStatusChangeModal}
        readOnlyRemark={activeRecord.status === AUDIT_STATUSES.ON_HOLD}
        buttonText={
          activeRecord.status === AUDIT_STATUSES.PENDING
            ? "auditListing.action.markOnHold"
            : "auditListing.action.markPending"
        }
        onSubmit={(reason) => {
          changeStatusOnSubmitFromModal({
            reason,
            status:
              activeRecord?.status === AUDIT_STATUSES.PENDING
                ? AUDIT_STATUSES?.ON_HOLD
                : AUDIT_STATUSES.PENDING,
          });
        }}
        placeholder={"auditListing.action.markOnHoldReasonPlaceholder"}
        title={getLocalizedMessage(
          intl,
          "auditListing.action.statusChangeModalTitle"
        )}
        message={
          activeRecord.status === AUDIT_STATUSES.ON_HOLD && activeRecord?.reason
        }
      />
      <SetAuditTypeContainer
        onSubmit={(values) => auditReAuditDateNMediumSubmit(values)}
        open={openActionModal}
        onClose={() => {
          setOpenActionModal(false);
          if (isReAudit) setIsReAudit(false);
        }}
        record={activeRecord}
        isReAudit={isReAudit}
      />
      <ConfirmModalContainer
        iconSrc={"/icons/warningIcon.svg"}
        question={getLocalizedMessage(
          intl,
          "auditListing.modal.canNotStartAudit"
        )}
        descriptionText={getLocalizedMessage(
          intl,
          "auditListing.modal.canNotStartAuditDesc"
        )}
        isCancelBtn={false}
        open={startAuditModal}
        restStyle={{ backgroundColor: variables.primaryBg }}
        confirmText={getLocalizedMessage(intl, "text.okay")}
        setOpen={setStartAuditModal}
      />
    </>
  );
};

export default AuditListingContainer;

function getAuditListQueryParams(query, filter, sortedInfo) {
  let queryParams = {
    pageLimit: query.recordsPerPage,
    pageSkip: query.recordsPerPage * (query.page - 1),
  };
  if (query.searchStr.length) {
    queryParams = {
      ...queryParams,
      searchStr: encodeURI(query.searchStr),
    };
  }
  if (filter.productCategories.length > 0) {
    queryParams = {
      ...queryParams,
      productCategoryId: filter.productCategories.join(","),
    };
  }
  if (filter.subCategories.length > 0) {
    queryParams = {
      ...queryParams,
      subProductCategoryId: filter.subCategories.join(","),
    };
  }
  if (filter.status.length > 0) {
    queryParams = {
      ...queryParams,
      status: filter.status.join(","),
    };
  }
  if (filter.countries.length > 0) {
    queryParams = {
      ...queryParams,
      countryId: filter.countries.join(","),
    };
  }
  if (filter.auditCreationYear.length > 0) {
    queryParams = {
      ...queryParams,
      year: filter.auditCreationYear.join(","),
    };
  }
  if (filter.currentAuditType.length > 0) {
    queryParams = {
      ...queryParams,
      auditMedium: filter.currentAuditType.join(","),
    };
  }
  if (sortedInfo?.columnKey) {
    queryParams = {
      ...queryParams,
      orderBy: sortedInfo.columnKey === "startDate" ? "start_date" : "end_date",
      order: sortedInfo.order === "ascend" ? 1 : -1,
    };
  }
  return queryParams;
}
